// Lib
import gql from 'graphql-tag';

// Types
import {
  ACCOUNT,
  ACCOUNT_V2_AUTH,
  AUTH,
  BACKGROUND_SETTINGS,
  CONTACT_DETAILS,
  PERSONAL_INFO,
  SHAREABLE_SETTINGS,
} from '../types';

const signInPhoneMutationString = `
  mutation signInPhone($input: AccountSignInPhoneInput!) {
    signInPhone(input: $input)
  }
`;

const signInPhoneVerifyMutationString = `
  mutation signInPhoneVerify($input: AccountSignInPhoneInput!) {
    signInPhoneVerify(input: $input) {
      account {
        ${ACCOUNT}
      } 
      tokens {
        access
      }
    }
  }
`;

export const verifyEmail = gql`
  mutation VerifyEmail($input: AccountVerifyEmailInput!) {
    verifyEmail(input: $input) {
      account {
        ${ACCOUNT_V2_AUTH}
      }
      tokens {
        access
      }
    }
  }
`;

export const verifyTempAccountCreadentialsUpdate = gql`
  mutation VerifyTempAccountCreadentialsUpdate($challenge: String) {
    verifyTempAccountCreadentialsUpdate(challenge: $challenge) {
      account {
        ${ACCOUNT}
      }
      tokens {
        access
      }
    }
  }
`;

export const signUpMutation = gql`
  mutation SignUp($input: AccountSignUpInput!) {
    signUp(input: $input)
  }
`;

export const signIn = gql`
  mutation SignIn($input: AccountSignInInput!) {
    signIn(input: $input) {
      account {
        ${ACCOUNT_V2_AUTH}
      }
      tokens {
        access
      }
    }
  }
`;

export const signInPhoneMutation = gql`
  ${signInPhoneMutationString}
`;

export const signInPhoneVerifyMutation = gql`
  ${signInPhoneVerifyMutationString}
`;

export const saveUserSettingsMutation = gql`
  mutation ChangeSettings($input: AccountSettingsFullInput) {
    changeUserSettings(input: $input) {
      username
      tagline
      fullName
      shareableSettings {
        ${SHAREABLE_SETTINGS}
      }
      backgroundSettings {
        ${BACKGROUND_SETTINGS}
      }
    }
  }
`;

export const signInGoogleMutation = gql`
  mutation signInGoogle($input: AccountSignInGoogleInput!) {
    signInGoogle(input: $input) {
      account {
        ${ACCOUNT_V2_AUTH}
      }
      tokens {
        access
      }
    }
  }
`;

export const logoutFromApp = gql`
  mutation AuthDisconnect($input: DisconnectAuthInput!) {
    authDisconnect(input: $input) {
      ${AUTH}
    }
  }
`;

export const checkUsername = gql`
  mutation checkUsername($input: AccountUsernameInput!) {
    checkUsername(input: $input)
  }
`;

export const changeUsername = gql`
  mutation changeUsername($input: AccountUsernameInput!) {
    changeUsername(input: $input) {
      username
    }
  }
`;

export const passOnboarding = gql`
  mutation {
    passOnboarding
  }
`;

export const selectFacebookManagedAuth = gql`
  mutation SelectFacebookManagedAuth($input: SelectFacebookManagedAuthsInput) {
    selectFacebookManagedAuth(input: $input)
  }
`;

export const changeTheme = gql`
  mutation ChangeTheme($input: AccountBackgroundSettingsInput) {
    changeUserBackgroundSettings(input: $input) {
      backgroundSettings {
        colorMode
        backgroundImage
      }
    }
  }
`;

export const changeAdvancedColors = gql`
  mutation ChangeAdvancedColors($input: AccountBackgroundSettingsInput) {
    changeUserBackgroundSettings(input: $input) {
      backgroundSettings {
        advancedColor {
          background
          secondary
          text
          icons
          line
          shadow1
          shadow2
          shadow3
          isActive
          primary
        }
      }
    }
  }
`;

export const shareMarbleLink = gql`
  mutation ShareMarbleLink($input: ShareMarbleLinkInput) {
    shareMarbleLink(input: $input)
  }
`;

export const changeMarkup = gql`
  mutation ChangeBackground($input: AccountBackgroundSettingsInput) {
    changeUserBackgroundSettings(input: $input) {
      backgroundSettings {
        markup {
          tabs {
            type
            name
            visible
            order
            extra {
              authId
              source
            }
          }
        }
      }
    }
  }
`;

export const changeSlideshowHidden = gql`
  mutation ChangeBackground($input: AccountBackgroundSettingsInput) {
    changeUserBackgroundSettings(input: $input) {
      backgroundSettings {
        slideshowHidden
      }
    }
  }
`;

export const updateDataCollectionSettings = gql`
  mutation UpdateDataCollectionSettings($input: DataCollectionSettingsInput) {
    updateDataCollectionSettings(input: $input)
  }
`;

export const createSubscription = gql`
  mutation CreateSubscription($input: AccountSubscriptionInput) {
    createSubscription(input: $input)
  }
`;

export const updateAccountFromLinktree = gql`
  mutation UpdateAccountFromLinktree($input: AccountFromLinktreeInput) {
    updateAccountFromLinktree(input: $input)
  }
`;

export const authConnectTokenless = gql`
  mutation AuthConnectTokenless($input: ConnectTokenlessInput) {
    authConnectTokenless(input: $input) {
      ${AUTH}
    }
  }
`;

export const refreshTokens = gql`
  mutation RefreshTokens {
    refreshTokens {
      code
    }
  }
`;

export const requestTempAccountCredentialsUpdate = gql`
  mutation RequestTempAccountCredentialsUpdate(
    $input: UpdateTempAccountCredentialsInput
  ) {
    requestTempAccountCredentialsUpdate(input: $input)
  }
`;

/**
 * Finish a forgot-password flow. `token` is the `:challenge` segment of the
 * `/auth/reset-password/:challenge` link from the reset email. The backend
 * consumes it here — do not "pre-validate" it with `resetPasswordTokenRequest`,
 * which burns the same one-time token.
 */
export const resetPasswordMutation = gql`
  mutation ResetPassword($token: String!, $input: AccountPasswordResetInput!) {
    resetPassword(token: $token, input: $input) {
      account {
        ${ACCOUNT_V2_AUTH}
      }
      tokens {
        access
      }
    }
  }
`;

/** In-app password change for a signed-in account. */
export const changePasswordMutation = gql`
  mutation ChangePassword($input: AccountPasswordChangeInput!) {
    changePassword(input: $input) {
      account {
        ${ACCOUNT_V2_AUTH}
      }
      tokens {
        access
      }
    }
  }
`;

export const accountContactEmailChangeRequest = gql`
  mutation AccountContactEmailChangeRequest($input: EmailChangeRequestInput) {
    accountContactEmailChangeRequest(input: $input)
  }
`;

export const accountContactEmailChange = gql`
  mutation AccountContactEmailChange($challenge: String) {
    accountContactEmailChange(challenge: $challenge) {
      account {
        ${ACCOUNT}
      }
      tokens {
        access
      }
    }
  }
`;

export const answerAccountQuiz = gql`
  mutation AnswerAccountQuiz($input: MarbleFormAnswerInput) {
    answerAccountQuiz(input: $input) {
      ${PERSONAL_INFO}
    }
  }
`;

export const accountPersonalTypeUpdate = gql`
  mutation AccountPersonalTypeUpdate($input: AccountPersonalInfoTypeUpdateInput) {
    accountPersonalTypeUpdate(input: $input) {
      ${ACCOUNT}
    }
  }
`;

export const accountContactDetailsUpdate = gql`
  mutation AccountContactDetailsUpdate($input: AccountContactDetailsUpdateInput) {
    accountContactDetailsUpdate(input: $input) {
      ${CONTACT_DETAILS}
    }
  }
`;

export const accountContactSend = gql`
  mutation AccountContactSend($input: AccountContactSendInput) {
    accountContactSend(input: $input)
  }
`;

export const updateAmbasadorSettings = gql`
  mutation UpdateAmbasadorSettings($input: AccountAmbassadorSettingsInput) {
    updateAmbasadorSettings(input: $input) {
      ${ACCOUNT}
    }
  }
`;
